/** Clair / sombre + accent — localStorage, comme Coffre / Mail.
 *  `plus` = peau Aurélia+ (FacturX seul) : palette bleu-violet fixe.
 *  Les PDF et les e-mails ne lisent pas ces variables.
 */
import { FX_ACCENTS, normalizeAccent, type FxAccentId } from "./accents";

export type FxTheme = "light" | "dark" | "plus";

export const FX_THEME_KEY = "facturx_theme";
export const FX_ACCENT_KEY = "facturx_accent";

export function readFxTheme(): FxTheme {
  try {
    const v = localStorage.getItem(FX_THEME_KEY);
    if (v === "light" || v === "dark" || v === "plus") return v;
  } catch {
    /* ignore */
  }
  return "light";
}

export function readFxAccent(): FxAccentId {
  try {
    return normalizeAccent(localStorage.getItem(FX_ACCENT_KEY));
  } catch {
    return "teal";
  }
}

export function persistFxTheme(theme: FxTheme): void {
  try {
    localStorage.setItem(FX_THEME_KEY, theme);
  } catch {
    /* ignore */
  }
}

export function persistFxAccent(accent: FxAccentId): void {
  try {
    localStorage.setItem(FX_ACCENT_KEY, accent);
  } catch {
    /* ignore */
  }
}

const PLUS_ONLY = ["--fx-glow", "--fx-glow-2"] as const;

export function applyFxTheme(theme: FxTheme, accentId: FxAccentId): void {
  const accent = FX_ACCENTS[normalizeAccent(accentId)];
  const plus = theme === "plus";
  const darkish = theme === "dark" || plus;
  const root = document.documentElement;
  const shell = document.getElementById("facturxAppShell");
  root.dataset.theme = theme;
  root.classList.toggle("dark", darkish);
  if (shell) {
    shell.classList.remove("fx-app");
    shell.dataset.theme = theme;
    shell.classList.toggle("dark", darkish);
  }
  const soft = theme === "dark" ? accent.accentSoftDark : accent.accentSoft;
  const vars: Record<string, string> = {
    "--fx-accent": accent.accent,
    "--fx-accent-strong": accent.accentStrong,
    "--fx-accent-soft": soft,
    "--fx-swatch": accent.swatch,
  };
  if (plus) {
    vars["--fx-bg"] = "#0b1220";
    vars["--fx-surface"] = "#151c2c";
    vars["--fx-surface-2"] = "#1a2336";
    vars["--fx-text"] = "#e8eef8";
    vars["--fx-muted"] = "#94a3b8";
    vars["--fx-border"] = "#2a3650";
    vars["--fx-aside-bg"] = "#0f172a";
    vars["--fx-aside-text"] = "#e2e8f0";
    vars["--fx-aside-muted"] = "#94a3b8";
    vars["--fx-accent"] = "#3b82f6";
    vars["--fx-accent-strong"] = "#c4b5fd";
    vars["--fx-accent-soft"] = "rgb(59 130 246 / 16%)";
    vars["--fx-swatch"] = "#3b82f6";
    vars["--fx-glow"] = "#3b82f6";
    vars["--fx-glow-2"] = "#7c3aed";
  } else if (theme === "dark") {
    vars["--fx-bg"] = "#0b1220";
    vars["--fx-surface"] = "#151c2c";
    vars["--fx-surface-2"] = "#1a2336";
    vars["--fx-text"] = "#e8eef8";
    vars["--fx-muted"] = "#94a3b8";
    vars["--fx-border"] = "#2a3650";
    vars["--fx-aside-bg"] = "#0f172a";
    vars["--fx-aside-text"] = "#e2e8f0";
    vars["--fx-aside-muted"] = "#94a3b8";
  } else {
    vars["--fx-bg"] = "#f3f5f4";
    vars["--fx-surface"] = "#ffffff";
    vars["--fx-surface-2"] = "#eef1f0";
    vars["--fx-text"] = "#134e4a";
    vars["--fx-muted"] = "#64748b";
    vars["--fx-border"] = "#e1e8e6";
    vars["--fx-aside-bg"] = "#ffffff";
    vars["--fx-aside-text"] = "#134e4a";
    vars["--fx-aside-muted"] = "#64748b";
  }
  if (!plus) {
    for (const k of PLUS_ONLY) {
      root.style.removeProperty(k);
      shell?.style.removeProperty(k);
    }
  }
  for (const [k, v] of Object.entries(vars)) {
    root.style.setProperty(k, v);
    shell?.style.setProperty(k, v);
  }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", plus || theme === "dark" ? "#0b1220" : accent.accent);
}

/** Fondu court au changement d’apparence. Pas au chargement. */
export function playFxThemeFade(): void {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const root = document.documentElement;
  root.classList.remove("fx-theme-swap");
  void root.offsetWidth;
  root.classList.add("fx-theme-swap");
  window.setTimeout(() => root.classList.remove("fx-theme-swap"), 760);
}

export function bootFxTheme(): void {
  applyFxTheme(readFxTheme(), readFxAccent());
}
